// Mic capture (PRD 001 F3): getUserMedia -> AudioWorklet -> 16-bit PCM frames +
// an RMS level. The worklet runs in its own global scope, so we ship it as a
// Blob URL string rather than a separate bundled file — this sidesteps every
// bundler/worklet-module pitfall.
//
// We request 16 kHz (which Chrome honours) and report back the actual rate so
// the provider config matches — no client-side resampling needed. Promoted from
// the Phase-0 spike (src/spike/audio.ts), validated end-to-end there.

const WORKLET_SOURCE = `
class PCMCapture extends AudioWorkletProcessor {
  constructor() {
    super()
    this.frame = 2048
    this.acc = new Float32Array(this.frame)
    this.idx = 0
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (!ch) return true
    for (let i = 0; i < ch.length; i++) {
      this.acc[this.idx++] = ch[i]
      if (this.idx === this.frame) {
        this.flush()
        this.idx = 0
      }
    }
    return true
  }
  flush() {
    const out = new Int16Array(this.frame)
    let sum = 0
    for (let i = 0; i < this.frame; i++) {
      const s = Math.max(-1, Math.min(1, this.acc[i]))
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      sum += this.acc[i] * this.acc[i]
    }
    const rms = Math.sqrt(sum / this.frame)
    this.port.postMessage({ pcm: out.buffer, rms }, [out.buffer])
  }
}
registerProcessor('pcm-capture', PCMCapture)
`

export interface MicCapture {
  /** Begins capture. Resolves with the actual sample rate of the stream. */
  start: (
    onFrame: (pcm: ArrayBuffer) => void,
    onLevel: (rms: number) => void
  ) => Promise<number>
  stop: () => Promise<void>
}

interface FramePayload {
  pcm: ArrayBuffer
  rms: number
}

export interface MicCaptureOptions {
  deviceId?: string
  /** A pre-acquired tab/system audio stream (getDisplayMedia) to mix into the
   *  capture — e.g. a Google Meet tab, so remote participants land in the
   *  transcript digitally instead of being recaptured (and AEC-cancelled) off
   *  the speakers. Its lifecycle is owned by the *caller*: we mix its live audio
   *  track but never stop it, so it survives pause/resume. */
  tabStream?: MediaStream | null
}

// Tab audio is mixed slightly under the mic so a loud call can't swamp the
// in-room voice when both hit full scale at once.
const TAB_GAIN = 0.9

export function createMicCapture(opts: MicCaptureOptions = {}): MicCapture {
  const { deviceId, tabStream } = opts
  let ctx: AudioContext | null = null
  let stream: MediaStream | null = null
  let micSource: MediaStreamAudioSourceNode | null = null
  let tabSource: MediaStreamAudioSourceNode | null = null
  let node: AudioWorkletNode | null = null
  let workletUrl: string | null = null

  async function start(
    onFrame: (pcm: ArrayBuffer) => void,
    onLevel: (rms: number) => void
  ): Promise<number> {
    ctx = new AudioContext({ sampleRate: 16000 })
    // Keep AEC on the mic even when mixing tab audio: Chrome still plays the
    // shared tab out the speakers, so AEC cancels that acoustic copy from the
    // mic path while the clean digital copy arrives via tabStream — no echo.
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    })

    workletUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: 'application/javascript' })
    )
    await ctx.audioWorklet.addModule(workletUrl)

    // Force the node mono so the summed mic + tab inputs down-mix to a single
    // channel the worklet reads — otherwise a stereo tab track would lose its
    // right half (the worklet only reads channel 0).
    node = new AudioWorkletNode(ctx, 'pcm-capture', {
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers'
    })
    node.port.onmessage = (e: MessageEvent<FramePayload>) => {
      onFrame(e.data.pcm)
      onLevel(e.data.rms)
    }

    micSource = ctx.createMediaStreamSource(stream)
    micSource.connect(node)

    // Mix in the remote tab audio if one was handed in and still live (the user
    // may have hit Chrome's "Stop sharing" between sessions).
    const tabTrack = tabStream
      ?.getAudioTracks()
      .find((t) => t.readyState === 'live')
    if (tabTrack) {
      tabSource = ctx.createMediaStreamSource(new MediaStream([tabTrack]))
      const tabGain = ctx.createGain()
      tabGain.gain.value = TAB_GAIN
      tabSource.connect(tabGain).connect(node)
    }

    // Connect through to the destination so the graph keeps pulling audio; the
    // worklet writes no output, so nothing is played back (no room feedback).
    node.connect(ctx.destination)

    return ctx.sampleRate
  }

  async function stop(): Promise<void> {
    node?.port.close()
    node?.disconnect()
    micSource?.disconnect()
    // Detach the tab source from our graph but DON'T stop its tracks — the
    // caller owns that stream so it can survive a pause/resume cycle.
    tabSource?.disconnect()
    stream?.getTracks().forEach((t) => t.stop())
    if (ctx) await ctx.close()
    if (workletUrl) URL.revokeObjectURL(workletUrl)
    ctx = null
    stream = null
    micSource = null
    tabSource = null
    node = null
    workletUrl = null
  }

  return { start, stop }
}

/** List available audio input devices. Labels require a prior permission grant. */
export async function listMics(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices.filter((d) => d.kind === 'audioinput')
}
